import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";
import { ArrowRight } from "lucide-react";

const sections = [
  {
    title: "Getting started",
    questions: [
      {
        q: "Do I need a gym membership to use FitEasy?",
        a: "Absolutely not! FitEasy is designed to work with whatever you have available. We offer bodyweight-only workouts that require zero equipment, home workout plans that use minimal equipment (like resistance bands or dumbbells), gym-based programs for those with access, and outdoor park classes that use natural elements. You can filter trainers and programs based on your equipment availability, and many trainers specialize in bodyweight training. We also offer equipment bundles if you want to enhance your home setup.",
      },
      {
        q: "Is there a free trial or money-back guarantee?",
        a: "Many trainers and programs offer free trial sessions or introductory rates—you can see these details in the app when browsing. Additionally, we offer a 7-day money-back guarantee on all monthly subscriptions if you're not satisfied with your experience. Simply contact our support team within the first week, and we'll process a full refund—no questions asked.",
      },
      {
        q: "What if I'm a complete beginner?",
        a: "Perfect! FitEasy is designed for people at all fitness levels, including complete beginners. When you sign up, you'll complete a comprehensive fitness assessment that helps us understand your starting point. We'll match you with trainers who specialize in working with beginners, and all programs can be scaled to your current ability. Many of our most successful members started as complete beginners—you're in good company!",
      },
    ],
  },
  {
    title: "Bookings & payments",
    questions: [
      {
        q: "How do bookings work on FitEasy?",
        a: "Booking is simple and straightforward. Browse available trainers, nutritionists, or park classes in the app, view their profiles, read reviews, check their availability calendar, and select a time slot that works for you. Once you confirm your booking, you'll receive all session details including location (if in-person), meeting links (if online), what to bring, and any preparation needed. All your upcoming sessions appear in your bookings tab, and you'll receive reminders 24 hours and 2 hours before each session.",
      },
      {
        q: "Can I cancel or reschedule my bookings?",
        a: "Yes! Cancellation and rescheduling policies vary by trainer or class, and all policies are clearly displayed before you confirm your booking. Generally, you can reschedule or cancel sessions up to 24 hours in advance through the app with no penalty. Late cancellations (within 24 hours) may incur a fee, which goes directly to the trainer to compensate for the lost time slot. For park classes, you can cancel up to 12 hours before class time. We recommend booking with trainers who offer flexible cancellation policies if your schedule is unpredictable.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit and debit cards (Visa, Mastercard, American Express), UPI payments, net banking, and digital wallets (Paytm, Google Pay, PhonePe). All payments are processed securely through encrypted payment gateways, and we never store your full card details. You can also set up automatic payments for monthly subscriptions to never miss a session.",
      },
    ],
  },
  {
    title: "Trainers, nutritionists & park classes",
    questions: [
      {
        q: "Are professionals on FitEasy verified and certified?",
        a: "Absolutely. Every trainer and nutritionist on FitEasy undergoes a rigorous verification process. This includes: credential verification (certifications must be from recognized organizations), background checks, professional reference checks, sample session review, and ongoing performance monitoring based on client feedback. All professionals must maintain a minimum 4.5-star rating to remain on the platform. You can see each professional's certifications, years of experience, and client ratings directly on their profile.",
      },
      {
        q: "What is QR check-in and how does it work?",
        a: "QR check-in is our seamless attendance tracking system for park classes and some in-person sessions. When you book a class, you'll receive a unique QR code in the app. Simply arrive at the park 5 minutes before class, open the FitEasy app, and scan your QR code at the instructor's device. This instantly confirms your attendance, protects your spot in popular classes, helps instructors manage class sizes, and automatically updates your workout history. It takes less than 5 seconds and eliminates the need for manual sign-in sheets.",
      },
      {
        q: "What types of park classes are available?",
        a: "We offer a wide variety of outdoor group fitness classes including: Bootcamp/HIIT sessions, Yoga and meditation flows, Strength training circuits, Mobility and stretching classes, Running and cardio groups, Functional fitness workouts, and Seasonal specialty classes. Classes are available at multiple times throughout the day (early morning, lunch break, evening) and are held in carefully curated parks across your city. You can filter classes by type, difficulty level, time, location, and instructor ratings.",
      },
    ],
  },
];

export default function FaqPage() {
  const breadcrumbData = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://fiteasy.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "FAQ",
        item: "https://fiteasy.com/faq",
      },
    ],
  };

  const faqStructuredData = {
    mainEntity: sections.flatMap((section) =>
      section.questions.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      }))
    ),
  };

  return (
    <>
      <SEO
        title="FAQ"
        description="Answers to common questions about getting started, bookings, payments, trainers, nutritionists, and park classes on FitEasy."
        path="/faq"
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <StructuredData type="FAQPage" data={faqStructuredData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

        <main id="main-content" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <SectionHeader
            id="faq-heading"
            label="FAQ"
            title="Frequently asked questions"
            description="If you don't see your question answered here, reach out via the contact page and our team will be happy to help."
            className="mb-10"
          />

          <section aria-labelledby="faq-heading" className="grid gap-8 md:grid-cols-3">
            {sections.map((section) => (
              <article
                key={section.title}
                className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2"
              >
                <h2 className="font-display text-lg text-gray-900">
                  {section.title}
                </h2>
                <div className="mt-4 space-y-6 text-sm text-gray-700" role="list">
                  {section.questions.map((item) => (
                    <div key={item.q} role="listitem">
                      <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.a}</p>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </section>

          <section className="mt-12 rounded-3xl border border-blue-200 bg-white/90 p-8 text-center">
            <h2 className="font-display text-2xl text-gray-900 mb-4">
              Still have questions?
            </h2>
            <p className="text-gray-700 mb-6">
              Our support team is here to help you get started.
            </p>
            <CTAButton
              href="/contact"
              variant="primary"
              ariaLabel="Contact FitEasy support"
            >
              Contact Us
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </CTAButton>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

